import { useState, useRef, useEffect, memo } from "react";
import PropTypes from "prop-types";
import { BIT_PROP_TYPES } from "@constants/best-in-travel/2025/proptypes";
import cn from "classnames";

import { useBestInTravel } from "@context/bestInTravel/2025/bestInTravelContext";

import { ImgixNextBackground } from "@components/imgixNextBackground";
import { Loading } from "@components/loading";

// Small screens: Maintains a precise 4:5 aspect ratio.
// Larger screens: The aspect ratio is approximately 1.41:1, closely resembling the traditional 1.43:1 ratio used in photography, often rounded to 3:2.

const sizes = {
  sm: { w: 500, h: 625 }, // 4:5 aspect ratio for small screens
  md: { w: 768, h: 512 }, // Approx. 3:2 aspect ratio
  lg: { w: 1080, h: 720 }, // Approx. 3:2 aspect ratio
  xl: { w: 1440, h: 960 }, // Approx. 3:2 aspect ratio
  "2xl": { w: 1920, h: 1280 }, // Approx. 3:2 aspect ratio
};

const ImageWrapper = memo(({ src, imgixParams = {} }) => {
  const { screen, loading, setLoading } = useBestInTravel();
  // TODO - move this to context to be able to set error state for all images with the controls
  const [error, setError] = useState(false);

  const handleLoad = () => {
    setLoading(false);
  };

  const handleError = () => {
    setLoading(false);
    setError(true);
  };

  // TODO - Evaluate the use of the screen useBestInTravel hook to allow us to move away from needing a JS screen evaluation

  // This is a hack to account for the 60px of image that extends below the content wrapper,
  // this will approximately shift the image up by a percentage of the image height roughly equivalent to 60px.
  const offset = 60 / sizes[screen].h;
  const defaultImgixParams = {
    "fp-y": 0.5 + offset, // default center is 0.5, plus the calculated offset to shift the center point up 60 px.
    auto: "format",
    fit: "crop",
    q: 75,
    ...sizes[screen],
  };

  const mergedImgixParams = imgixParams
    ? { ...defaultImgixParams, ...imgixParams }
    : defaultImgixParams;

  return (
    <div className="absolute top-0 object-cover w-full h-full">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center xl:text-black bg-black-100">
          <Loading />
        </div>
      )}
      <ImgixNextBackground
        lazyload="true"
        src={src}
        imgixParams={mergedImgixParams}
        className={cn(
          "absolute top-0 object-cover w-full h-full transition-opacity duration-1000 ",
          loading ? "opacity-0" : "opacity-100"
        )}
        onLoad={handleLoad}
        onError={handleError}
      />
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black-200">
          <div className="px-4 py-2 text-xs text-white bg-transparent-60 rounded-pill">
            Images not available
          </div>
        </div>
      )}
    </div>
  );
});

ImageWrapper.propTypes = {
  src: PropTypes.string.isRequired,
  imgixParams: BIT_PROP_TYPES.imgixParams,
};

const VideoWrapper = memo(({ src }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const videoElement = videoRef.current;

    const playVideo = async () => {
      if (videoElement && videoElement.paused) {
        try {
          await videoElement.play();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Error playing video:", error);
        }
      }
    };

    playVideo();

    return () => {
      if (videoElement && !videoElement.paused) {
        videoElement.pause();
      }
    };
  }, [src]);

  return (
    /* eslint-disable-next-line jsx-a11y/media-has-caption */
    <video
      ref={videoRef}
      className="absolute top-0 object-cover w-full h-full"
      autoPlay
      loop
      muted
      poster={src?.poster}
      playsInline
    >
      <source src={src?.mp4} type="video/mp4" />
      <source src={src?.webm} type="video/webm" />
    </video>
  );
});

VideoWrapper.propTypes = {
  src: PropTypes.shape({
    poster: PropTypes.string.isRequired,
    mp4: PropTypes.string,
    webm: PropTypes.string,
  }),
};

export function Background({ media }) {
  const MediaWrapper = media?.type === "image" ? ImageWrapper : VideoWrapper;

  return (
    <div className="relative h-[calc(100vh-162px)] sm:h-screen-dvh">
      <MediaWrapper {...media} />
    </div>
  );
}

Background.propTypes = {
  media: PropTypes.shape({
    type: PropTypes.oneOf(["video", "image"]).isRequired,
    orientation: PropTypes.oneOf(["portrait", "landscape"]),
    credit: PropTypes.string,
    src: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        poster: PropTypes.string,
        mp4: PropTypes.string,
        webm: PropTypes.string,
      }),
    ]).isRequired,
    imgixParams: BIT_PROP_TYPES.imgixParams,
  }).isRequired,
};
